import { Link } from "@commonsku/styles";
import { useMemo } from "react";
import React from "react";

type AlertType = "loading" | "success" | "neutral" | "error" | "warn";

interface UseShipStationAlertParams {
  statusCode: number | null;
}

interface UseShipStationAlertReturn {
  alertMessage: React.ReactNode;
  alertType: AlertType;
}

const ShipStationLink = () => (
  <Link as="a" href="https://www.shipstation.com/docs/api/" target="_blank">
    ShipStation account.
  </Link>
);

const ERROR_MESSAGES: Record<
  number,
  { message: React.ReactNode; type: AlertType }
> = {
  200: {
    message: <>Connection Successful</>,
    type: "success",
  },
  400: {
    message: (
      <>
        Connection failed. Find your API Key and Secret pair in your&nbsp;
        <ShipStationLink />
      </>
    ),
    type: "error",
  },
  403: {
    message: (
      <>Connection failed. You are not authorized to complete this request.</>
    ),
    type: "error",
  },
  500: {
    message: (
      <>
        Connection failed. An error occurred while connecting to ShipStation.
        Please contact support if the problem persists.
      </>
    ),
    type: "error",
  },
};

export function useShipStationAlert({
  statusCode,
}: UseShipStationAlertParams): UseShipStationAlertReturn {
  return useMemo(() => {
    if (statusCode !== null && statusCode in ERROR_MESSAGES) {
      const errorMessage = ERROR_MESSAGES[statusCode];
      return {
        alertMessage: errorMessage.message,
        alertType: errorMessage.type,
      };
    }

    return {
      alertMessage: (
        <>
          Find your API Key and Secret pair in your&nbsp;
          <ShipStationLink />
        </>
      ),
      alertType: "neutral",
    };
  }, [statusCode]);
}
